import React, { CSSProperties, FC, HTMLInputTypeAttribute, MutableRefObject, ReactNode } from 'react';
import { FieldRenderProps } from 'react-final-form';
import classNames from 'classnames';

import isMobile from 'src/common/utils/isMobile';
import getClassName from 'src/common/utils/getClassName';
import FieldWrapper from '../FieldWrapper';
import s from './Input.module.scss';

export type InputProps = FieldRenderProps<string, HTMLInputElement> & {
  autoFocus?: boolean;
  autoResize?: boolean;
  classNameModifier?: string;
  disabled?: boolean;
  externalError?: string;
  fieldRef?: MutableRefObject<HTMLInputElement>;
  id?: string;
  label?: ReactNode;
  max?: number;
  min?: number;
  placeholder?: string;
  shouldBeTouchedToShowError?: boolean;
  style?: CSSProperties;
  textareaRef?: MutableRefObject<HTMLTextAreaElement>;
  type?: HTMLInputTypeAttribute;
  warning?: string;
};

const Input: FC<InputProps> = ({
  id,
  input,
  meta,
  placeholder,
  classNameModifier,
  disabled,
  autoFocus,
  type = 'text',
  fieldRef,
  min,
  max,
  label,
  shouldBeTouchedToShowError,
  externalError, // API error
  style = {},
  warning,
}) => {
  const anyError = externalError || meta.error;
  const modifiers = classNames(classNameModifier, { error: meta.touched && meta.error });

  return (
    <FieldWrapper
      id={id || input.name}
      touched={meta.touched}
      error={anyError}
      warning={warning}
      label={label}
      rootClassName={getClassName(s, 'root', modifiers)}
      errorClassName={getClassName(s, 'error', modifiers)}
      shouldBeTouchedToShowError={shouldBeTouchedToShowError}
    >
      <input
        id={id || input.name}
        type={type}
        pattern={type === 'number' ? '\\d*' : undefined}
        disabled={disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={!isMobile() && autoFocus}
        placeholder={placeholder}
        ref={fieldRef}
        className={getClassName(s, 'field', modifiers)}
        min={min}
        max={max}
        style={style}
        {...input}
      />
    </FieldWrapper>
  );
};

export default Input;
